// External Dependencies
import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// Icons
import { IoArrowBackOutline, IoClose, IoAdd, IoTrashOutline } from 'react-icons/io5'
import { FiUpload, FiChevronDown } from 'react-icons/fi'

// Custom Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

// Components
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

// Constants
const TEXTURE_CATEGORIES = [
  { value: 'Stone', label: 'Stone' },
  { value: 'Brick', label: 'Brick' },
  { value: 'Wood', label: 'Wood' },
  { value: 'Terrazzo', label: 'Terrazzo' },
  { value: 'Concrete', label: 'Concrete' },
  { value: 'Metal', label: 'Metal' },
  { value: 'Tile', label: 'Tile' },
  { value: 'Fabric', label: 'Fabric' },
  { value: 'Wallpaper', label: 'Wallpaper' },
  { value: 'Carpet', label: 'Carpet' },
  { value: 'Surfaces', label: 'Surfaces' },
  { value: 'Finishes', label: 'Finishes' },
  { value: 'Landscaping', label: 'Landscaping' },
  { value: 'Insulation', label: 'Insulation' },
  { value: 'Organic', label: 'Organic' },
  { value: 'Acoustic', label: 'Acoustic' },
]

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg']
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const PAGE_SIZE = 10 // Changed to 10 for pagination

// Custom Dropdown Component with Infinite Scroll
const CustomSelect = ({
  label,
  options,
  value,
  onChange,
  error,
  placeholder,
  loading,
  hasMore,
  onScroll,
  disabled = false,
  required = false,
  renderOption,
  getOptionLabel,
  getOptionValue,
  loadingText = 'Loading more...',
  noOptionsText = 'No options available'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const listRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter options based on search
  const filteredOptions = searchTerm
    ? options.filter(opt =>
      getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options

  const selectedOption = options.find(opt => getOptionValue(opt) === value)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle scroll for infinite loading
  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight - 20 && !loading && hasMore) {
      onScroll?.()
    }
  }

  const handleSelect = (option) => {
    onChange(getOptionValue(option))
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div className='w-full' ref={dropdownRef}>
      {label && (
        <label className='block text-sm font-medium mb-2'>
          {label} {required && <span className='text-red-500'>*</span>}
        </label>
      )}
      <div className='relative'>
        {/* Trigger Button */}
        <button
          type='button'
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full border-[2px] ${error ? 'border-red-500' : 'border-light-gray'} 
            rounded-lg p-3 focus:outline-none focus:border-prime 
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
            flex items-center justify-between text-left`}
        >
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
            {selectedOption ? getOptionLabel(selectedOption) : placeholder || 'Select...'}
          </span>
          <FiChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown List */}
        {isOpen && !disabled && (
          <div className='absolute z-50 w-full mt-1 bg-white border border-light-gray rounded-lg shadow-lg overflow-hidden'>
            {/* Search Input */}
            <div className='p-2 border-b border-light-gray'>
              <input
                type='text'
                placeholder='Search...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full border rounded-lg p-2 text-sm focus:outline-none focus:border-prime'
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Options List */}
            <ul
              ref={listRef}
              onScroll={handleListScroll}
              className='max-h-60 overflow-y-auto'
            >
              {filteredOptions.length === 0 ? (
                <li className='px-4 py-2 text-gray-500 text-sm text-center'>
                  {searchTerm ? 'No matching options' : noOptionsText}
                </li>
              ) : (
                filteredOptions.map((option, index) => (
                  <li
                    key={getOptionValue(option) || index}
                    onClick={() => handleSelect(option)}
                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-prime/10 transition-colors
                      ${getOptionValue(option) === value ? 'bg-prime/10 text-prime' : 'text-gray-700'}`}
                  >
                    {renderOption ? renderOption(option) : getOptionLabel(option)}
                  </li>
                ))
              )}
              {loading && (
                <li className='px-4 py-2 text-gray-400 text-sm text-center'>
                  {loadingText}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className='text-red-500 text-sm mt-1'>{error}</p>}
    </div>
  )
}

const SeamlessTextureAdd = () => {
  const navigate = useNavigate()
  const GoBack = useGoBack()
  const userPermissions = useUserStore((state) => state.user) || []
  const fileInputRef = useRef(null)
  const galleryInputRefs = useRef({})

  // Dropdown data with pagination
  const [patterns, setPatterns] = useState([])
  const [materials, setMaterials] = useState([])
  const [brands, setBrands] = useState([])
  const [joints, setJoints] = useState([])

  // Pagination states - Pattern
  const [patternPage, setPatternPage] = useState(1)
  const [patternHasMore, setPatternHasMore] = useState(true)
  const [patternLoading, setPatternLoading] = useState(false)

  // Pagination states - Material
  const [materialPage, setMaterialPage] = useState(1)
  const [materialHasMore, setMaterialHasMore] = useState(true)
  const [materialLoading, setMaterialLoading] = useState(false)

  // Pagination states - Joint
  const [jointPage, setJointPage] = useState(1)
  const [jointHasMore, setJointHasMore] = useState(true)
  const [jointLoading, setJointLoading] = useState(false)

  const [isUploading, setIsUploading] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    category: 'Select Category',
    brand: '',
    pattern: '',
    material: '',
    joint: '',
    description: '',
    image_description: '',
    alt_tag: '',
    color: '',
    width_mm: '',
    height_mm: '',
    thumbnailFile: null,
    thumbnailPreview: null,
  })

  // Datasheet structure
  const [datasheet, setDatasheet] = useState([])
  const [errors, setErrors] = useState({})

  // ---------- Fetch Functions with Pagination ----------
  const fetchPatterns = async (page = 1, append = true) => {
    if (patternLoading || !patternHasMore) return
    setPatternLoading(true)
    try {
      const response = await axiosInterceptorInstance.post('/patterns', {
        page,
        limit: PAGE_SIZE,
        front: false
      })
      const results = response.data?.data?.results || response.data?.results || []
      const totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1

      if (append) {
        setPatterns(prev => [...prev, ...results])
      } else {
        setPatterns(results)
      }

      setPatternHasMore(page < totalPages)
      setPatternPage(page)
    } catch (err) {
      CustomToast({ message: 'Failed to load patterns.', type: 'error' })
    } finally {
      setPatternLoading(false)
    }
  }

  const fetchMaterials = async (page = 1, append = true) => {
    if (materialLoading || !materialHasMore) return
    setMaterialLoading(true)
    try {
      const response = await axiosInterceptorInstance.post('/materials', {
        page,
        limit: PAGE_SIZE,
        front: false
      })
      const results = response.data?.data?.results || response.data?.results || []
      const totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1
      if (append) {
        setMaterials(prev => [...prev, ...results])
      } else {
        setMaterials(results)
      }

      setMaterialHasMore(page < totalPages)
      setMaterialPage(page)
    } catch (err) {
      CustomToast({ message: 'Failed to load materials.', type: 'error' })
    } finally {
      setMaterialLoading(false)
    }
  }

  const fetchJoints = async (page = 1, append = true) => {
    if (jointLoading || !jointHasMore) return
    setJointLoading(true)
    try {
      const response = await axiosInterceptorInstance.post('/materials?&category=joint', {
        page,
        limit: PAGE_SIZE,
        category: 'joint'
      })
      const results = response.data?.data?.results || response.data?.results || []
      const totalPages = response.data?.data?.totalPages || response.data?.totalPages || 1


      if (append) {
        setJoints(prev => [...prev, ...results])
      } else {
        setJoints(results)
      }

      setJointHasMore(page < totalPages)
      setJointPage(page)
    } catch (err) {
      CustomToast({ message: 'Failed to load joints.', type: 'error' })
    } finally {
      setJointLoading(false)
    }
  }

  const fetchBrands = async () => {
    try {
      const response = await axiosInterceptorInstance.post('/brands', { page: 1, limit: 200 })
      const results = response.data?.data?.results || response.data?.results || []
      setBrands(results)
    } catch (err) {
      CustomToast({ message: 'Failed to load brands.', type: 'error' })
    }
  }

  // Load all dropdown data - initial load
  useEffect(() => {
    fetchPatterns(1, false)
    fetchMaterials(1, false)
    fetchJoints(1, false)
    fetchBrands()
  }, [])

  // ---------- Filtered Materials by Category ----------
  const filteredMaterials = materials.filter(material => {
    if (formData.category === 'Select Category') return true
    return material.category === formData.category
  })

  // ---------- Auto-select brand when material changes ----------
  useEffect(() => {
    if (formData.material) {
      const selectedMaterial = materials.find(m => m._id === formData.material)
      if (selectedMaterial?.brand?._id) {
        setFormData(prev => ({
          ...prev,
          brand: selectedMaterial.brand._id
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          brand: ''
        }))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        brand: ''
      }))
    }
  }, [formData.material, materials])

  // ---------- Load More Functions ----------
  const loadMorePatterns = () => {
    if (!patternLoading && patternHasMore) {
      fetchPatterns(patternPage + 1, true)
    }
  }

  const loadMoreMaterials = () => {
    if (!materialLoading && materialHasMore) {
      fetchMaterials(materialPage + 1, true)
    }
  }

  const loadMoreJoints = () => {
    if (!jointLoading && jointHasMore) {
      fetchJoints(jointPage + 1, true)
    }
  }

  // ---------- Validation ----------
  const validateInputs = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = 'Name is required.'
    if (formData.category === 'Select Category') newErrors.category = 'Please select a valid category.'
    if (!formData.pattern) newErrors.pattern = 'Pattern is required.'
    if (!formData.material) newErrors.material = 'Material is required.'
    if (!formData.thumbnailFile) newErrors.file = 'Please upload a thumbnail image.'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // ---------- Thumbnail Handlers ----------
  const handleImageChange = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, file: 'Invalid file type. Only JPG, PNG, and JPEG are accepted.' }))
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({ ...prev, file: 'File size must be less than 5MB.' }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      thumbnailFile: file,
      thumbnailPreview: URL.createObjectURL(file),
    }))
    setErrors((prev) => ({ ...prev, file: null }))
  }

  const handleDeleteImage = () => {
    setFormData((prev) => ({ ...prev, thumbnailFile: null, thumbnailPreview: null }))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ---------- Datasheet Handlers ----------
  const addDatasheetItem = () => {
    setDatasheet((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        label: '',
        values: [
          {
            id: Date.now() + Math.random(),
            type: 'line',
            lineValue: '',
            galleryFiles: [],
            galleryPreviews: [],
          },
        ],
      },
    ])
  }

  const removeDatasheetItem = (itemId) => {
    setDatasheet((prev) => prev.filter((item) => item.id !== itemId))
  }

  const updateLabel = (itemId, newLabel) => {
    setDatasheet((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, label: newLabel } : item))
    )
  }

  const addValue = (itemId) => {
    setDatasheet((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item
        return {
          ...item,
          values: [
            ...item.values,
            {
              id: Date.now() + Math.random(),
              type: 'line',
              lineValue: '',
              galleryFiles: [],
              galleryPreviews: [],
            },
          ],
        }
      })
    )
  }

  const removeValue = (itemId, valueId) => {
    setDatasheet((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item
        return {
          ...item,
          values: item.values.filter((v) => v.id !== valueId),
        }
      })
    )
  }

  const updateValue = (itemId, valueId, field, value) => {
    setDatasheet((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item
        return {
          ...item,
          values: item.values.map((v) => {
            if (v.id !== valueId) return v

            if (field === 'type') {
              return {
                ...v,
                type: value,
                lineValue: value === 'line' ? v.lineValue : '',
                galleryFiles: value === 'gallery' ? v.galleryFiles : [],
                galleryPreviews: value === 'gallery' ? v.galleryPreviews : [],
              }
            }
            return { ...v, [field]: value }
          }),
        }
      })
    )
  }

  const handleGalleryChange = (itemId, valueId, event) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    const validFiles = []
    const previews = []

    for (const file of files) {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        CustomToast({ message: `${file.name} is not a valid image type.`, type: 'error' })
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        CustomToast({ message: `${file.name} is larger than 5MB.`, type: 'error' })
        continue
      }
      validFiles.push(file)
      previews.push(URL.createObjectURL(file))
    }

    if (validFiles.length === 0) return

    setDatasheet((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item
        return {
          ...item,
          values: item.values.map((v) => {
            if (v.id !== valueId) return v
            return {
              ...v,
              galleryFiles: [...v.galleryFiles, ...validFiles],
              galleryPreviews: [...v.galleryPreviews, ...previews],
            }
          }),
        }
      })
    )

    if (galleryInputRefs.current[`${itemId}_${valueId}`]) {
      galleryInputRefs.current[`${itemId}_${valueId}`].value = ''
    }
  }

  const removeGalleryImage = (itemId, valueId, imageIndex) => {
    setDatasheet((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item
        return {
          ...item,
          values: item.values.map((v) => {
            if (v.id !== valueId) return v
            return {
              ...v,
              galleryFiles: v.galleryFiles.filter((_, i) => i !== imageIndex),
              galleryPreviews: v.galleryPreviews.filter((_, i) => i !== imageIndex),
            }
          }),
        }
      })
    )
  }

  // ---------- Submit ----------
  const handleSubmit = async () => {
    if (!userPermissions.includes('seamless-texture:create')) {
      CustomToast({ message: 'You do not have permission to create seamless textures.', type: 'error' })
      return
    }

    if (!validateInputs()) return

    setIsUploading(true)

    try {
      const fd = new FormData()
      fd.append('name', formData.name.trim())
      fd.append('category', formData.category)
      fd.append('brand', formData.brand || '')
      fd.append('pattern', formData.pattern)
      fd.append('material', formData.material)
      if (formData.joint) fd.append('joint', formData.joint)

      fd.append('description', formData.description.trim())
      fd.append('image_description', formData.image_description.trim())
      fd.append('alt_tag', formData.alt_tag.trim())
      fd.append('color', formData.color.trim())
      if (formData.width_mm) fd.append('width_mm', formData.width_mm)
      if (formData.height_mm) fd.append('height_mm', formData.height_mm)

      fd.append('file', formData.thumbnailFile)

      const filteredDatasheet = datasheet
        .filter(item => item.label.trim() !== '')
        .map(item => ({
          label: item.label.trim(),
          values: item.values
            .filter(val => {
              if (val.type === 'line') return val.lineValue.trim() !== ''
              if (val.type === 'gallery') return val.galleryFiles.length > 0
              return false
            })
            .map(val => ({
              type: val.type,
              value: val.type === 'line' ? val.lineValue : [],
            }))
        }))
        .filter(item => item.values.length > 0)

      if (filteredDatasheet.length > 0) {
        fd.append('datasheet', JSON.stringify(filteredDatasheet))

        datasheet.forEach((item) => {
          if (item.label.trim() === '') return
          item.values.forEach((val) => {
            if (val.type === 'gallery' && val.galleryFiles.length > 0) {
              val.galleryFiles.forEach((file) => {
                fd.append('gallery', file)
              })
            }
          })
        })
      }

      await axiosInterceptorInstance.post('/seamless-textures/upload', fd)

      CustomToast({ message: 'Seamless texture created successfully.', type: 'success' })
      navigate('/seamless-texture')
    } catch (error) {
      CustomToast({
        message: error.response?.data?.message || 'Failed to create seamless texture. Please try again!',
        type: 'error',
      })
    } finally {
      setIsUploading(false)
    }
  }

  // ---------- Reusable Field ----------
  const field = (id, label, value, key, type = 'text', placeholder = '') => (
    <div className='w-full sm:w-[500px]'>
      <label className='block text-sm font-medium mb-2' htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder || label}
        className={`w-full border-[2px] ${errors[key] ? 'border-red-500' : 'border-light-gray'
          } rounded-lg p-3 focus:outline-none focus:border-prime`}
        value={value}
        onChange={(e) => {
          setFormData((prev) => ({ ...prev, [key]: e.target.value }))
          setErrors((prev) => ({ ...prev, [key]: null }))
        }}
      />
      {errors[key] && <p className='text-red-500 text-sm mt-1'>{errors[key]}</p>}
    </div>
  )

  // ---------- Helper functions for CustomSelect ----------
  const getPatternLabel = (p) => p.name || p._id
  const getPatternValue = (p) => p._id

  const getMaterialLabel = (m) => m.name || m._id
  const getMaterialValue = (m) => m._id

  const getJointLabel = (j) => j.name || j._id
  const getJointValue = (j) => j._id

  const getBrandLabel = (b) => b.name || b._id
  const getBrandValue = (b) => b._id

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={GoBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Add Seamless Texture</h1>
          </div>
        </div>
      </PageHeader>

      <div className='space-y-6 flex flex-col items-center pb-16'>
        {/* Name */}
        {field('name', 'Texture Name:', formData.name, 'name', 'text', 'e.g. Herringbone Brick')}

        {/* Category */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2' htmlFor='category'>
            Select Category:
          </label>
          <select
            id='category'
            className={`w-full border-[2px] ${errors.category ? 'border-red-500' : 'border-light-gray'
              } rounded-lg p-3 focus:outline-none focus:border-prime`}
            value={formData.category}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                category: e.target.value,
                material: '',
                brand: ''
              }))
              setErrors((prev) => ({ ...prev, category: null }))
            }}
          >
            <option disabled value='Select Category'>
              Select Category
            </option>
            {TEXTURE_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          {errors.category && <p className='text-red-500 text-sm mt-1'>{errors.category}</p>}
        </div>

        {/* Material - Custom Select with Pagination */}
        <div className='w-full sm:w-[500px]'>
          <CustomSelect
            label='Select Material (Tile):'
            options={filteredMaterials}
            value={formData.material}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, material: value }))
              setErrors(prev => ({ ...prev, material: null }))
            }}
            error={errors.material}
            placeholder='Select Material'
            loading={materialLoading}
            hasMore={materialHasMore}
            onScroll={loadMoreMaterials}
            required={true}
            getOptionLabel={getMaterialLabel}
            getOptionValue={getMaterialValue}
            noOptionsText={
              formData.category === 'Select Category'
                ? 'Please select a category first'
                : 'No materials found for this category'
            }
            loadingText='Loading more materials...'
          />
        </div>

        {/* Brand - Auto-filled, Read-only */}
        <div className='w-full sm:w-[500px]'>
          <CustomSelect
            label='Brand: (Auto-selected from material)'
            options={brands}
            value={formData.brand}
            onChange={() => { }}
            placeholder='Select Brand'
            disabled={true}
            getOptionLabel={getBrandLabel}
            getOptionValue={getBrandValue}
          />
          {formData.material && !formData.brand && (
            <p className='text-yellow-500 text-sm mt-1'>Selected material has no brand associated.</p>
          )}
        </div>

        {/* Pattern - Custom Select with Pagination */}
        <div className='w-full sm:w-[500px]'>
          <CustomSelect
            label='Select Pattern:'
            options={patterns}
            value={formData.pattern}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, pattern: value }))
              setErrors(prev => ({ ...prev, pattern: null }))
            }}
            error={errors.pattern}
            placeholder='Select Pattern'
            loading={patternLoading}
            hasMore={patternHasMore}
            onScroll={loadMorePatterns}
            required={true}
            getOptionLabel={getPatternLabel}
            getOptionValue={getPatternValue}
            loadingText='Loading more patterns...'
          />
        </div>

        {/* Joint - Custom Select with Pagination */}
        <div className='w-full sm:w-[500px]'>
          <CustomSelect
            label='Select Joint:'
            options={joints}
            value={formData.joint}
            onChange={(value) => setFormData(prev => ({ ...prev, joint: value }))}
            placeholder='Select Joint'
            loading={jointLoading}
            hasMore={jointHasMore}
            onScroll={loadMoreJoints}
            getOptionLabel={getJointLabel}
            getOptionValue={getJointValue}
            loadingText='Loading more joints...'
            noOptionsText='No joints available'
          />
        </div>

        {/* Description */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>Description:</label>
          <textarea
            rows={3}
            placeholder='Enter description...'
            className='w-full border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime resize-none'
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          />
        </div>

        {/* Image Description */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>Image Description:</label>
          <textarea
            rows={3}
            placeholder='Enter image description...'
            className='w-full border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime resize-none'
            value={formData.image_description}
            onChange={(e) => setFormData((prev) => ({ ...prev, image_description: e.target.value }))}
          />
        </div>

        {/* Alt Tag */}
        {field('alt_tag', 'Alt Tag:', formData.alt_tag, 'alt_tag', 'text', 'e.g. A seamless organic texture...')}

        {/* Color */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>Color:</label>
          <div className='flex gap-3 items-center'>
            <input
              type='color'
              value={formData.color || '#bcb6b0'}
              onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
              className='w-12 h-12 rounded cursor-pointer border border-light-gray'
            />
            <input
              type='text'
              placeholder='#bcb6b0'
              className='flex-1 border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime'
              value={formData.color}
              onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
            />
          </div>
        </div>

        {/* Width & Height */}
        <div className='w-full sm:w-[500px] flex gap-4'>
          <div className='flex-1'>
            <label className='block text-sm font-medium mb-2'>Width (mm):</label>
            <input
              type='number'
              placeholder='e.g. 1000'
              className='w-full border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime'
              value={formData.width_mm}
              onChange={(e) => setFormData((prev) => ({ ...prev, width_mm: e.target.value }))}
            />
          </div>
          <div className='flex-1'>
            <label className='block text-sm font-medium mb-2'>Height (mm):</label>
            <input
              type='number'
              placeholder='e.g. 665'
              className='w-full border-[2px] border-light-gray rounded-lg p-3 focus:outline-none focus:border-prime'
              value={formData.height_mm}
              onChange={(e) => setFormData((prev) => ({ ...prev, height_mm: e.target.value }))}
            />
          </div>
        </div>

        {/* Datasheet Section */}
        <div className='w-full sm:w-[500px]'>
          <div className='flex items-center justify-between mb-3'>
            <label className='block text-sm font-medium'>
              Datasheet: <span className='text-xs text-gray-400 font-normal'>(Optional)</span>
            </label>
            <button
              type='button'
              onClick={addDatasheetItem}
              className='flex items-center gap-1 text-sm text-prime hover:underline'
            >
              <IoAdd size={18} />
              Add Label
            </button>
          </div>

          <div className='space-y-5'>
            {datasheet.length === 0 ? (
              <div className='text-center py-8 border-2 border-dashed border-light-gray rounded-xl'>
                <p className='text-gray-400 text-sm'>No datasheet entries yet. Click "Add Label" to add one.</p>
              </div>
            ) : (
              datasheet.map((item, itemIndex) => (
                <div
                  key={item.id}
                  className='border-2 border-light-gray rounded-xl p-4 relative bg-white'
                >
                  {datasheet.length > 1 && (
                    <button
                      type='button'
                      onClick={() => removeDatasheetItem(item.id)}
                      className='absolute top-3 right-3 text-red-500 hover:text-red-700'
                      title='Remove Label'
                    >
                      <IoTrashOutline size={18} />
                    </button>
                  )}

                  <div className='mb-4'>
                    <label className='block text-xs font-medium text-gray-600 mb-1'>Label</label>
                    <input
                      type='text'
                      placeholder='e.g. Images, Thickness, Dimensions...'
                      className='w-full border-[2px] border-light-gray rounded-lg p-2.5 text-sm focus:outline-none focus:border-prime'
                      value={item.label}
                      onChange={(e) => updateLabel(item.id, e.target.value)}
                    />
                  </div>

                  <div className='space-y-3'>
                    {item.values.map((val, valIndex) => (
                      <div
                        key={val.id}
                        className='border border-gray-200 rounded-lg p-3 bg-gray-50 relative'
                      >
                        {item.values.length > 1 && (
                          <button
                            type='button'
                            onClick={() => removeValue(item.id, val.id)}
                            className='absolute top-2 right-2 text-red-400 hover:text-red-600'
                            title='Remove Value'
                          >
                            <IoClose size={16} />
                          </button>
                        )}

                        <div className='mb-2'>
                          <label className='block text-xs font-medium text-gray-500 mb-1'>Type</label>
                          <select
                            className='w-full border border-light-gray rounded-lg p-2 text-sm focus:outline-none focus:border-prime'
                            value={val.type}
                            onChange={(e) => updateValue(item.id, val.id, 'type', e.target.value)}
                          >
                            <option value='line'>Line (Text)</option>
                            <option value='gallery'>Gallery (Images)</option>
                          </select>
                        </div>

                        {val.type === 'line' ? (
                          <div>
                            <label className='block text-xs font-medium text-gray-500 mb-1'>Value</label>
                            <input
                              type='text'
                              placeholder='Enter value...'
                              className='w-full border-[2px] border-light-gray rounded-lg p-2 text-sm focus:outline-none focus:border-prime'
                              value={val.lineValue}
                              onChange={(e) => updateValue(item.id, val.id, 'lineValue', e.target.value)}
                            />
                          </div>
                        ) : (
                          <div>
                            <label className='block text-xs font-medium text-gray-500 mb-1'>Images</label>

                            {val.existingImages?.length > 0 && (
                              <div className='mb-2 grid grid-cols-3 gap-2'>
                                {val.existingImages.map((img, imgIndex) => (
                                  <div key={img.id || imgIndex} className='relative aspect-square rounded-md overflow-hidden border'>
                                    <img
                                      src={img.url}
                                      alt={img.name || `Image ${imgIndex + 1}`}
                                      className='w-full h-full object-cover'
                                    />
                                    <button
                                      type='button'
                                      onClick={() => removeGalleryImage(item.id, val.id, imgIndex, true)}
                                      className='absolute top-1 right-1 w-5 h-5 bg-black/70 text-white rounded-full flex items-center justify-center hover:bg-red-600'
                                    >
                                      <IoClose size={12} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className='relative border-2 border-dashed border-prime rounded-lg p-3 flex flex-col items-center justify-center cursor-pointer hover:bg-prime/5 transition'>
                              <input
                                ref={(el) => (galleryInputRefs.current[`${item.id}_${val.id}`] = el)}
                                type='file'
                                accept='image/jpeg,image/png,image/jpg'
                                multiple
                                className='absolute inset-0 opacity-0 cursor-pointer'
                                onChange={(e) => handleGalleryChange(item.id, val.id, e)}
                              />
                              <FiUpload className='text-xl text-prime mb-1' />
                              <span className='text-xs text-prime'>Upload more images</span>
                            </div>

                            {val.galleryPreviews.length > 0 && (
                              <div className='mt-2 grid grid-cols-3 gap-2'>
                                {val.galleryPreviews.map((preview, imgIndex) => (
                                  <div key={imgIndex} className='relative aspect-square rounded-md overflow-hidden border'>
                                    <img src={preview} alt={`New ${imgIndex + 1}`} className='w-full h-full object-cover' />
                                    <button
                                      type='button'
                                      onClick={() => removeGalleryImage(item.id, val.id, imgIndex, false)}
                                      className='absolute top-1 right-1 w-5 h-5 bg-black/70 text-white rounded-full flex items-center justify-center hover:bg-red-600'
                                    >
                                      <IoClose size={12} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      type='button'
                      onClick={() => addValue(item.id)}
                      className='w-full py-1.5 text-sm text-prime border border-dashed border-prime rounded-lg hover:bg-prime/5 transition'
                    >
                      + Add Value
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Thumbnail Upload */}
        <div className='w-full sm:w-[500px]'>
          <label className='block text-sm font-medium mb-2'>Upload Material:</label>
          <div className='relative'>
            <div
              className={`relative w-full aspect-[9/5] rounded-lg bg-primary bg-opacity-5 cursor-pointer border ${errors.file ? 'border-red-500' : 'border-dashed border-prime'
                } flex items-center justify-center`}
            >
              {!formData.thumbnailPreview && (
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  className='absolute inset-0 opacity-0 cursor-pointer'
                  onChange={handleImageChange}
                  aria-label='Upload thumbnail image'
                />
              )}
              {formData.thumbnailPreview ? (
                <img
                  src={formData.thumbnailPreview}
                  alt='Thumbnail preview'
                  className='absolute top-0 left-0 object-cover w-full h-full rounded-lg'
                />
              ) : (
                <div className='flex flex-col gap-4 items-center'>
                  <FiUpload className='text-4xl text-white bg-prime rounded-full cursor-pointer p-2' />
                  <span className='text-prime'>Only JPG, PNG, and JPEG files are accepted</span>
                </div>
              )}
              {formData.thumbnailFile && (
                <button
                  type='button'
                  onClick={handleDeleteImage}
                  className='absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-prime bg-opacity-80 rounded-full text-primary bg-white'
                  aria-label='Remove thumbnail'
                >
                  <IoClose />
                </button>
              )}
            </div>
            {errors.file && <p className='text-red-500 text-sm mt-1'>{errors.file}</p>}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className='w-full sm:w-[500px] py-3 rounded-md text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-opacity-80 disabled:text-white'
        >
          {isUploading ? 'Creating...' : 'Create Seamless Texture'}
        </button>
      </div>
    </div>
  )
}

export default SeamlessTextureAdd